import {
  STORAGE_KEYS,
  apiErrorMessage,
  customFetch,
} from '@agos/api-client'
import type { BranchOption, BranchOptions, LoginResponse, SetupStatus } from '@agos/api-client/model'
import { T, franchiseNameOf, landingFor, tt, useAuth, useBranding, useTheme } from '@agos/shared'
import { ChefHat, Eye, EyeOff, Lock, Mail, Phone, User } from 'lucide-react'
import { useEffect, useState, type FormEvent } from 'react'
import {  } from '@agos/ui'
import { useLocation } from 'wouter'

/**
 * Ported 1:1 from legacy `pages/login.tsx` — the four modes, the branch picker,
 * the first-run setup path and the Control Tower entry.
 *
 * The palette here is deliberately *not* the design system's. Legacy's login
 * screen carries its own dark slate and amber, unrelated to the tokens the rest
 * of the app uses. That is an inconsistency (FRONTEND-BUGS.md FB-010), but it
 * is the first screen every member of staff sees every morning, so it is
 * carried over exactly and the normalization is a §9.3 decision to take against
 * the screenshot pair — not one to slip in during a port.
 */

/**
 * The platform's own name, for a login screen whose franchise the Host did not resolve - §9.700.
 *
 * This defaulted to *PizzaMajstro*, the owner's brand, so Sereď's staff signed in under another restaurant's
 * name every morning. The screen now leads with the franchise the Host resolved (`franchiseNameOf`), and this
 * is what is left when even that is unknown: a deployment's own override, else the platform.
 */
const APP_NAME = import.meta.env.VITE_APP_NAME ?? 'AGOS'

type LoginMode = 'login' | 'setup' | 'register' | 'super_admin'

const SELF_REGISTRATION_ROLES = [
  { value: 'waiter', label: tt('Čašník'), emoji: '🍽️' },
  { value: 'driver', label: tt('Kuriér'), emoji: '🛵' },
  { value: 'cook', label: tt('Kuchár'), emoji: '👨‍🍳' },
] as const

type SelfRegistrationRole = (typeof SELF_REGISTRATION_ROLES)[number]['value']

// `BranchOption` and the setup flag were hand-written here; both come from the backend now
// (BLOCKED #4), so a branch that gains a field cannot silently go unread.

const INPUT_CLASSES =
  'w-full bg-[#0f1117] border border-white/10 rounded-lg px-4 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-amber-400/50 focus:ring-1 focus:ring-amber-400/30'
const INPUT_WITH_ICON_CLASSES =
  'w-full bg-[#0f1117] border border-white/10 rounded-lg pl-9 pr-4 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-amber-400/50 focus:ring-1 focus:ring-amber-400/30'
const SUBMIT_CLASSES =
  'w-full bg-amber-400 hover:bg-amber-500 disabled:opacity-50 text-black font-semibold rounded-lg py-2.5 transition-colors'
const ERROR_CLASSES =
  'bg-red-500/10 border border-red-500/30 rounded-lg px-4 py-2.5 text-red-400 text-sm'
const LABEL_CLASSES = 'block text-sm text-gray-400 mb-1.5'


/** The role the session just written to storage carries. Null if anything about it is unreadable. */
function signedInRole(): string | undefined {
  try {
    const raw = localStorage.getItem(STORAGE_KEYS.user)
    if (raw === null) return undefined
    const parsed: unknown = JSON.parse(raw)
    const role = (parsed as { systemRole?: unknown }).systemRole
    return typeof role === 'string' ? role : undefined
  } catch {
    return undefined
  }
}

/** `localStorage`, or nothing on a browser that has it switched off. */
function safeStorage(): Storage | null {
  try {
    return window.localStorage
  } catch {
    return null
  }
}

export function LoginPage() {
  const { login, loginSuperAdmin, applySession } = useAuth()
  const theme = useTheme()
  const { branding } = useBranding()
  const [, navigate] = useLocation()

  const [mode, setMode] = useState<LoginMode>('login')
  const [needsSetup, setNeedsSetup] = useState<boolean | null>(null)
  /* FB-846, §9.560 - **a separate flag, because `null` is also *not asked yet*.** The catch below used to collapse an
     unanswered check to a definite **no**, which hid the only route to creating a franchise's first administrator; but
     leaving it null cannot be told from the initial state, so the link would flash on every login page load. */
  const [setupUnknown, setSetupUnknown] = useState(false)
  const [branchesUnknown, setBranchesUnknown] = useState(false)
  /**
   * Public self-registration is permanently off — franchises and managers are
   * created through the Control Tower. Ported as legacy has it: the constant
   * documents the decision and keeps the branch compiling, and there is no UI
   * path to it.
   */
  const selfRegistrationEnabled = false

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [branches, setBranches] = useState<BranchOption[]>([])
  const [pickedBranchId, setPickedBranchId] = useState<number | null>(null)
  // The picked branch, falling back to whichever one the Host resolved to.
  const branchId = pickedBranchId ?? branding?.id ?? null

  const [setupName, setSetupName] = useState('')
  const [setupEmail, setSetupEmail] = useState('')
  const [setupPassword, setSetupPassword] = useState('')
  const [setupConfirm, setSetupConfirm] = useState('')

  const [registrationFirstName, setRegistrationFirstName] = useState('')
  const [registrationLastName, setRegistrationLastName] = useState('')
  const [registrationPhone, setRegistrationPhone] = useState('')
  const [registrationEmail, setRegistrationEmail] = useState('')
  const [registrationPassword, setRegistrationPassword] = useState('')
  const [registrationConfirm, setRegistrationConfirm] = useState('')
  const [registrationRole, setRegistrationRole] = useState<SelfRegistrationRole | ''>('')

  // Which branches this manager domain serves. Several branches can share one
  // domain, and the operator picks which one they are signing in to.
  useEffect(() => {
    void (async () => {
      try {
        const options = await customFetch<BranchOptions>('/api/v1/central/branch-options')
        if (options.branches.length) setBranches(options.branches)
        setBranchesUnknown(false)
      } catch {
        /* A single-branch domain answers nothing useful, so the picker staying hidden is right for one branch - and
           indistinguishable from a failure, because the picker is gated on `branches.length > 1`. FB-846, §9.560: the
           sign-in then falls back to the Host-resolved branch, so an operator on a shared domain would sign into
           whichever franchise the Host names rather than the one they work at. Said rather than silent. */
        setBranchesUnknown(true)
      }
    })()
  }, [])


  /**
   * Pin the chosen branch before the sign-in request goes out, so the
   * `X-Franchise-Id` header names the branch the operator picked. The tenant
   * middleware honours it within the domain's manager group, which is what
   * authenticates against that branch's database (BACKEND.md §5).
   */
  useEffect(() => {
    if (branchId == null) return
    try {
      localStorage.setItem(STORAGE_KEYS.franchiseId, String(branchId))
    } catch {
      // The header falls back to the Host-resolved branch.
    }
  }, [branchId])

  useEffect(() => {
    void (async () => {
      try {
        const status = await customFetch<SetupStatus>('/api/v1/auth/check-setup')
        setNeedsSetup(Boolean(status?.needsSetup))
        setSetupUnknown(false)
      } catch {
        /* FB-846, §9.560 - **the route is offered rather than hidden, because the server is the authority.**
           `StaffAuthService.setup` throws 409 *Hlavný admin už existuje* the moment one exists, documented as the only
           thing standing between a fresh deployment and whoever finds it first. So offering it on an unknown state
           costs a refusal at worst; hiding it locks somebody out of their own new installation. */
        setSetupUnknown(true)
      }
    })()
  }, [])

  const returnToLogin = () => {
    setMode('login')
    setError('')
  }

  const handleSignIn = async (event: FormEvent) => {
    event.preventDefault()
    setError('')
    setIsSubmitting(true)
    try {
      if (mode === 'super_admin') await loginSuperAdmin(email, password)
      else await login(email, password)
      // §9.112 — the account's role is the only authority now; there is no tile to reconcile it
      // against. `login` resolves void and the context's `user` is a render behind, so the role is
      // read from where `setUser` has just written it, the same place the shell reads on every boot.
      navigate(landingFor(signedInRole(), null, safeStorage()).path)
    } catch (caught) {
      setError(apiErrorMessage(caught, tt('Prihlásenie zlyhalo')))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleFirstRunSetup = async (event: FormEvent) => {
    event.preventDefault()
    setError('')
    if (setupPassword !== setupConfirm) {
      setError(tt('Heslá sa nezhodujú'))
      return
    }
    setIsSubmitting(true)
    try {
      const session = await customFetch<LoginResponse>('/api/v1/auth/setup', {
        method: 'POST',
        body: JSON.stringify({ name: setupName, email: setupEmail, password: setupPassword }),
      })
      applySession(session.user ?? {}, session.token ?? '')
      setNeedsSetup(false)
      // FB-846, §9.560 - the franchise is set up now, so the unknown state is resolved with it.
      setSetupUnknown(false)
      navigate('/dashboard')
    } catch (caught) {
      setError(apiErrorMessage(caught, tt('Chyba pri vytváraní účtu')))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSelfRegistration = async (event: FormEvent) => {
    event.preventDefault()
    setError('')
    if (!registrationRole) {
      setError(tt('Vyberte svoju rolu'))
      return
    }
    if (registrationPassword !== registrationConfirm) {
      setError(tt('Heslá sa nezhodujú'))
      return
    }
    setIsSubmitting(true)
    try {
      const session = await customFetch<LoginResponse>('/api/v1/auth/register', {
        method: 'POST',
        body: JSON.stringify({
          firstName: registrationFirstName,
          lastName: registrationLastName,
          phone: registrationPhone,
          email: registrationEmail,
          password: registrationPassword,
          role: registrationRole,
        }),
      })
      applySession(session.user ?? {}, session.token ?? '')
      navigate('/dashboard')
    } catch (caught) {
      setError(apiErrorMessage(caught, tt('Chyba pri registrácii')))
    } finally {
      setIsSubmitting(false)
    }
  }

  // The dark logo on a dark screen, the light one otherwise — ported key names,
  // because the design-settings page writes them.
  const logoStorageKey = theme.mode === 'dark' ? 'fastkitchen_logo_light' : 'fastkitchen_logo'
  const savedLogo = (() => {
    try {
      return localStorage.getItem(logoStorageKey)
    } catch {
      return null
    }
  })()

  return (
    <div className="min-h-screen bg-[#0f1117] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          {savedLogo ? (
            <img src={savedLogo} alt="Logo" className="h-16 mx-auto mb-4 object-contain" />
          ) : (
            <div className="flex items-center justify-center gap-2 mb-4">
              <ChefHat className="text-amber-400" size={40} />
              <span className="text-3xl font-bold text-white" data-login-brand>
                {franchiseNameOf(branding) || APP_NAME}
              </span>
            </div>
          )}
          <p className="text-gray-400 text-sm">
            <T>Systém správy reštaurácie</T>
          </p>
        </div>

        <div className="bg-[#1a1d27] rounded-2xl border border-white/10 p-8 shadow-2xl">
          {(mode === 'login' || mode === 'super_admin') && (
            <>
              <h1 className="text-xl font-semibold text-white mb-2">
                {mode === 'super_admin' ? 'Control Tower – Super Admin' : tt('Prihlásenie')}
              </h1>

              {mode === 'login' &&
                branding &&
                (branches.length > 1 ? (
                  <div className="mb-4">
                    <label className={LABEL_CLASSES} htmlFor="login-pobocka">
                      <T>Pobočka</T>
                    </label>
                    <select
                      id="login-pobocka"
                      value={branchId ?? branding.id}
                      onChange={(event) => setPickedBranchId(Number(event.target.value))}
                      className={INPUT_CLASSES}
                    >
                      {branches.map((branch) => (
                        <option key={branch.id} value={branch.id}>
                          {branch.name}
                        </option>
                      ))}
                    </select>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500 mb-4">
                    <T>Pobočka:</T>{' '}
                    <span className="text-amber-400 font-medium">
                      {branding.displayName ?? branding.name}
                    </span>
                    {branchesUnknown && (
                      <span className="block text-amber-400/80 mt-1" data-branches-unknown>
                        <T>
                          Zoznam pobočiek sa nepodarilo načítať. Ak ich má táto adresa viac, prihlásite sa do tej
                          vyššie.
                        </T>
                      </span>
                    )}
                  </p>
                ))}

              {/* §9.1's role tiles were here and are gone - §9.112, the owner's point 1.
                * Legacy has no picker on this screen: its three tiles live in the registration form,
                * above a constant reading "Public employee self-registration is permanently off", so
                * they never render. The account's own role decides where you land. */}
              <form onSubmit={(event) => void handleSignIn(event)} className="space-y-4">
                <div>
                  <label className={LABEL_CLASSES} htmlFor="login-email">Email</label>
                  <div className="relative">
                    <Mail
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
                      size={16}
                    />
                    <input
                      id="login-email"
                      type="email"
                      value={email}
                      onChange={(event) => setEmail(event.target.value)}
                      required
                      placeholder="vas@email.sk"
                      className={INPUT_WITH_ICON_CLASSES}
                    />
                  </div>
                </div>

                <div>
                  <label className={LABEL_CLASSES} htmlFor="login-heslo">
                    <T>Heslo</T>
                  </label>
                  <div className="relative">
                    <Lock
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
                      size={16}
                    />
                    <input
                      id="login-heslo"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      required
                      placeholder="••••••••"
                      className="w-full bg-[#0f1117] border border-white/10 rounded-lg pl-9 pr-10 py-2.5 text-white placeholder-gray-600 focus:outline-none focus:border-amber-400/50 focus:ring-1 focus:ring-amber-400/30"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((shown) => !shown)}
                      aria-label={tt('Zobraziť heslo')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                {error && <div className={ERROR_CLASSES}>{error}</div>}

                <button type="submit" disabled={isSubmitting} className={SUBMIT_CLASSES}>
                  {isSubmitting ? tt('Prihlasujem...') : tt('Prihlásiť sa')}
                </button>
              </form>

              <div className="mt-6 pt-6 border-t border-white/10 text-center space-y-2">
                {selfRegistrationEnabled && (
                  <p className="text-gray-500 text-xs">
                    <T>Nemáte účet?</T>{' '}
                    <button
                      type="button"
                      onClick={() => {
                        setMode('register')
                        setError('')
                      }}
                      className="text-amber-400 hover:text-amber-300"
                    >
                      <T>Vytvoriť si účet</T>
                    </button>
                  </p>
                )}
                {setupUnknown && (
                  <p className="text-amber-400/80 text-xs" data-setup-unknown>
                    <T>
                      Nepodarilo sa zistiť, či je táto prevádzka už nastavená. Ak je nová, vytvorte hlavného admina -
                      ak už existuje, server to odmietne.
                    </T>{' '}
                    <button
                      type="button"
                      onClick={() => {
                        setMode('setup')
                        setError('')
                      }}
                      className="text-amber-400 hover:text-amber-300 underline"
                    >
                      <T>Vytvoriť hlavného admina</T>
                    </button>
                  </p>
                )}
                {needsSetup === true && (
                  <p className="text-gray-500 text-xs">
                    <T>Prvé spustenie?</T>{' '}
                    <button
                      type="button"
                      onClick={() => {
                        setMode('setup')
                        setError('')
                      }}
                      className="text-amber-400 hover:text-amber-300"
                    >
                      <T>Vytvoriť hlavného admina</T>
                    </button>
                  </p>
                )}
                <p className="text-gray-500 text-xs">
                  <button
                    type="button"
                    onClick={() => {
                      setMode(mode === 'super_admin' ? 'login' : 'super_admin')
                      setError('')
                    }}
                    className="text-amber-400 hover:text-amber-300"
                  >
                    {mode === 'super_admin' ? (
                      <T>← Späť na pobočkové prihlásenie</T>
                    ) : (
                      <T>Prihlásiť sa ako Super Admin</T>
                    )}
                  </button>
                </p>
              </div>
            </>
          )}

          {mode === 'setup' && (
            <>
              <div className="flex items-center gap-2 mb-6">
                <button
                  type="button"
                  onClick={returnToLogin}
                  className="text-gray-400 hover:text-white text-sm"
                >
                  <T>← Späť</T>
                </button>
                <h1 className="text-xl font-semibold text-white">
                  <T>Prvé nastavenie</T>
                </h1>
              </div>
              <p className="text-gray-400 text-sm mb-6">
                <T>
                  Vytvorte účet hlavného administrátora. Táto možnosť je dostupná len pri prvom
                  spustení.
                </T>
              </p>
              <form onSubmit={(event) => void handleFirstRunSetup(event)} className="space-y-4">
                <div>
                  <label className={LABEL_CLASSES} htmlFor="login-cele-meno">
                    <T>Celé meno</T>
                  </label>
                  <input
                    id="login-cele-meno"
                    type="text"
                    value={setupName}
                    onChange={(event) => setSetupName(event.target.value)}
                    required
                    placeholder={tt('Ján Novák')}
                    className={INPUT_CLASSES}
                  />
                </div>
                <div>
                  <label className={LABEL_CLASSES} htmlFor="login-email-2">Email</label>
                  <input
                    id="login-email-2"
                    type="email"
                    value={setupEmail}
                    onChange={(event) => setSetupEmail(event.target.value)}
                    required
                    placeholder={tt('admin@reštauracia.sk')}
                    className={INPUT_CLASSES}
                  />
                </div>
                <div>
                  <label className={LABEL_CLASSES} htmlFor="login-heslo-2">
                    <T>Heslo</T>
                  </label>
                  <input
                    id="login-heslo-2"
                    type="password"
                    value={setupPassword}
                    onChange={(event) => setSetupPassword(event.target.value)}
                    required
                    minLength={6}
                    placeholder="min. 6 znakov"
                    className={INPUT_CLASSES}
                  />
                </div>
                <div>
                  <label className={LABEL_CLASSES} htmlFor="login-potvrdit-heslo">
                    <T>Potvrdiť heslo</T>
                  </label>
                  <input
                    id="login-potvrdit-heslo"
                    type="password"
                    value={setupConfirm}
                    onChange={(event) => setSetupConfirm(event.target.value)}
                    required
                    placeholder={tt('Zopakujte heslo')}
                    className={INPUT_CLASSES}
                  />
                </div>

                {error && <div className={ERROR_CLASSES}>{error}</div>}

                <button type="submit" disabled={isSubmitting} className={SUBMIT_CLASSES}>
                  {isSubmitting ? tt('Vytváram...') : tt('Vytvoriť účet a prihlásiť sa')}
                </button>
              </form>
            </>
          )}

          {mode === 'register' && (
            <>
              <div className="flex items-center gap-2 mb-6">
                <button
                  type="button"
                  onClick={returnToLogin}
                  className="text-gray-400 hover:text-white text-sm"
                >
                  <T>← Späť</T>
                </button>
                <h1 className="text-xl font-semibold text-white">
                  <T>Registrácia</T>
                </h1>
              </div>
              <form onSubmit={(event) => void handleSelfRegistration(event)} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={LABEL_CLASSES} htmlFor="login-meno">
                      <T>Meno</T>
                    </label>
                    <div className="relative">
                      <User
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
                        size={16}
                      />
                      <input
                        id="login-meno"
                        type="text"
                        value={registrationFirstName}
                        onChange={(event) => setRegistrationFirstName(event.target.value)}
                        required
                        placeholder={tt('Ján')}
                        className={INPUT_WITH_ICON_CLASSES}
                      />
                    </div>
                  </div>
                  <div>
                    <label className={LABEL_CLASSES} htmlFor="login-priezvisko">
                      <T>Priezvisko</T>
                    </label>
                    <input
                      id="login-priezvisko"
                      type="text"
                      value={registrationLastName}
                      onChange={(event) => setRegistrationLastName(event.target.value)}
                      required
                      placeholder={tt('Novák')}
                      className={INPUT_CLASSES}
                    />
                  </div>
                </div>

                <div>
                  <label className={LABEL_CLASSES} htmlFor="login-telefonne-cislo">
                    <T>Telefónne číslo</T>
                  </label>
                  <div className="relative">
                    <Phone
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
                      size={16}
                    />
                    <input
                      id="login-telefonne-cislo"
                      type="tel"
                      value={registrationPhone}
                      onChange={(event) => setRegistrationPhone(event.target.value)}
                      required
                      placeholder="+421 900 000 000"
                      className={INPUT_WITH_ICON_CLASSES}
                    />
                  </div>
                </div>

                <div>
                  <label className={LABEL_CLASSES} htmlFor="login-email-3">Email</label>
                  <div className="relative">
                    <Mail
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
                      size={16}
                    />
                    <input
                      id="login-email-3"
                      type="email"
                      value={registrationEmail}
                      onChange={(event) => setRegistrationEmail(event.target.value)}
                      required
                      placeholder="vas@email.sk"
                      className={INPUT_WITH_ICON_CLASSES}
                    />
                  </div>
                </div>

                <div>
                  <label className={LABEL_CLASSES} htmlFor="login-heslo-3">
                    <T>Heslo</T>
                  </label>
                  <div className="relative">
                    <Lock
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
                      size={16}
                    />
                    <input
                      id="login-heslo-3"
                      type="password"
                      value={registrationPassword}
                      onChange={(event) => setRegistrationPassword(event.target.value)}
                      required
                      minLength={6}
                      placeholder="min. 6 znakov"
                      className={INPUT_WITH_ICON_CLASSES}
                    />
                  </div>
                </div>

                <div>
                  <label className={LABEL_CLASSES} htmlFor="login-potvrdit-heslo-2">
                    <T>Potvrdiť heslo</T>
                  </label>
                  <div className="relative">
                    <Lock
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500"
                      size={16}
                    />
                    <input
                      id="login-potvrdit-heslo-2"
                      type="password"
                      value={registrationConfirm}
                      onChange={(event) => setRegistrationConfirm(event.target.value)}
                      required
                      placeholder={tt('Zopakujte heslo')}
                      className={INPUT_WITH_ICON_CLASSES}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-gray-400 mb-2">
                    <T>Vaša rola</T>
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {SELF_REGISTRATION_ROLES.map((role) => (
                      <button
                        key={role.value}
                        type="button"
                        onClick={() => setRegistrationRole(role.value)}
                        className={`flex flex-col items-center gap-1.5 py-3 px-2 rounded-xl border text-sm font-medium transition-all ${
                          registrationRole === role.value
                            ? 'border-amber-400 bg-amber-400/10 text-amber-400'
                            : 'border-white/10 bg-[#0f1117] text-gray-400 hover:border-white/20'
                        }`}
                      >
                        <span className="text-xl">{role.emoji}</span>
                        <span>
                          <T>{role.label}</T>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>

                {error && <div className={ERROR_CLASSES}>{error}</div>}

                <button type="submit" disabled={isSubmitting} className={SUBMIT_CLASSES}>
                  {isSubmitting ? tt('Vytváram účet...') : tt('Vytvoriť účet')}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
